<template>
  <b-card>
    <b-form>
      <b-row>
        <!-- Old Password -->
        <b-col md="6">
          <b-form-group label="Old Password" label-for="account-old-password">
            <b-input-group class="input-group-merge">
              <b-form-input
                id="account-old-password"
                v-model="passwordValueOld"
                name="old-password"
                :type="passwordFieldTypeOld"
                placeholder="Old Password"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="passwordToggleIconOld"
                  class="cursor-pointer"
                  @click="togglePasswordOld"
                />
              </b-input-group-append>
            </b-input-group>
            <small v-if="errors.oldPassword" class="text-danger">
              {{ errors.oldPassword }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <!-- New Password -->
        <b-col md="6">
          <b-form-group label-for="account-new-password">
            <label class="d-flex align-items-center">
              New Password
              <feather-icon
                icon="AlertCircleIcon"
                size="12"
                class="cursor-pointer ml-1"
                v-b-tooltip.hover.focus.v-primary
                title="Your password must be at least 6 characters, including a digit, lowercase, uppercase, and special character."
                variant="outline-primary"
              />
            </label>
            <b-input-group class="input-group-merge">
              <b-form-input
                id="account-new-password"
                v-model="newPasswordValue"
                :type="passwordFieldTypeNew"
                name="new-password"
                placeholder="New Password"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="passwordToggleIconNew"
                  class="cursor-pointer"
                  @click="togglePasswordNew"
                />
              </b-input-group-append>
            </b-input-group>
            <small v-if="errors.newPassword" class="text-danger">
              {{ errors.newPassword }}
            </small>
          </b-form-group>
        </b-col>

        <!-- Retype New Password -->
        <b-col md="6">
          <b-form-group label-for="account-retype-new-password">
            <label>Retype New Password</label>
            <b-input-group class="input-group-merge">
              <b-form-input
                id="account-retype-new-password"
                v-model="retypePassword"
                :type="passwordFieldTypeRetype"
                name="retype-password"
                placeholder="Retype New Password"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="passwordToggleIconRetype"
                  class="cursor-pointer"
                  @click="togglePasswordRetype"
                />
              </b-input-group-append>
            </b-input-group>
            <small v-if="errors.retypePassword" class="text-danger">
              {{ errors.retypePassword }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <!-- Buttons -->
        <b-col cols="12">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-1 mr-1"
            @click="changePassword()"
          >
            <b-spinner label="Spinning" v-if="showSpinner"></b-spinner>
            Change Password
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="reset"
            variant="outline-secondary"
            class="mt-1"
            @click="reset()"
          >
            Reset
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import api from "@/api/api-v1-config";
import { mapGetters, mapActions } from "vuex";
import { customError, customSwal, customLoader } from "@/libs/customFn";

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      passwordValueOld: "",
      newPasswordValue: "",
      retypePassword: "",
      passwordFieldTypeOld: "password",
      passwordFieldTypeNew: "password",
      passwordFieldTypeRetype: "password",
      token: localStorage.getItem("accessToken"),
      showSpinner: false,
      errors: {},
    };
  },
  computed: {
    passwordToggleIconOld() {
      return this.passwordFieldTypeOld === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    passwordToggleIconNew() {
      return this.passwordFieldTypeNew === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    passwordToggleIconRetype() {
      return this.passwordFieldTypeRetype === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    ...mapGetters("auth", ["user"]),
  },
  methods: {
    togglePasswordOld() {
      this.passwordFieldTypeOld = this.passwordFieldTypeOld === "password" ? "text" : "password";
    },
    togglePasswordNew() {
      this.passwordFieldTypeNew = this.passwordFieldTypeNew === "password" ? "text" : "password";
    },
    togglePasswordRetype() {
      this.passwordFieldTypeRetype = this.passwordFieldTypeRetype === "password" ? "text" : "password";
    },

    validatePassword() {
      this.errors = {};
      const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;

      if (!this.passwordValueOld.trim()) {
        this.errors.oldPassword = "Old Password is required";
      }
      if (!this.newPasswordValue.trim()) {
        this.errors.newPassword = "New Password is required";
      } else if (!passwordPattern.test(this.newPasswordValue)) {
        this.errors.newPassword =
          "New Password must be at least 6 characters, including a digit, lowercase, uppercase, and a special character.";
      }
      if (!this.retypePassword.trim()) {
        this.errors.retypePassword = "Retype New Password is required";
      } else if (this.newPasswordValue !== this.retypePassword) {
        this.errors.retypePassword = "New passwords do not match";
      }
      if (this.passwordValueOld === this.newPasswordValue) {
    this.errors.newPassword = "New password cannot be the same as old password";
  }
      return Object.keys(this.errors).length === 0;
    },

    async changePassword() {
      if (!this.validatePassword()) {
        return;
      }
      let loader = customLoader('Please wait while updating your password');
      this.showSpinner = true;
      let data = {
        password: this.passwordValueOld,
        newPassword: this.newPasswordValue,
        confirmNewPassword: this.retypePassword,
      };

      this.changePasswordAction(data)
        .then((response) => {
          customSwal(response.data.message);
          this.showSpinner = false;
          loader.close();
        })
        .catch((error) => {
          customError(error.response.data);
          this.showSpinner = false;
          loader.close();
        });
    },

    ...mapActions("personalModule", ["changePasswordAction"]),
  },
};
</script>
